"use client";

import { useEffect, useRef } from "react";

/**
 * Voert `laad` uit zodra de bezoeker iets doet (tikken, scrollen met muiswiel, klikken, toets) of,
 * zonder interactie, `rustMs` na het laden van de pagina. Eén keer per mount.
 *
 * Waarom: de overzichtspagina's (shop, categorie) halen na hydratatie de volledige
 * productlijst op (honderden KB aan Firestore-data die React daarna ook nog verwerkt).
 * Dat gebeurde midden in het laden en liep voor de instellingen (pop-up, teksten) en
 * de hydratatie uit: op een mobiele verbinding 1 à 5 seconden extra wachttijd en een
 * geblokkeerde hoofdthread. De eerste pagina staat al in de HTML; de rest is pas nodig
 * als er iets met de lijst gebeurt (filteren, sorteren, pagina 2), en een aanraking of
 * scroll gaat daaraan vooraf.
 *
 * `direct` = true start meteen (er is geen eerste pagina meegegeven, dus er valt niets
 * uit te stellen).
 */
// Alleen gebeurtenissen die een bezoeker veroorzaakt. Bewust géén "scroll" of "focusin": die
// worden ook door de browser, een scriptmatig scrollen of een automatisch gefocuste knop
// afgevuurd (de laadtest van de sitecontrole startte de lading daardoor al na 1,2 s). Scrollen
// begint altijd met een aanraking (touchstart), muiswiel (wheel), toets (keydown) of klik.
const INTERACTIE_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"] as const;

export function useLaadNaInteractie(laad: () => void, opties: { direct?: boolean; rustMs?: number } = {}) {
  const { direct = false, rustMs = 4000 } = opties;
  // Altijd de nieuwste `laad` (met de actuele state), zonder het effect elke render opnieuw te starten.
  const laadRef = useRef(laad);
  useEffect(() => {
    laadRef.current = laad;
  });

  useEffect(() => {
    if (direct) {
      laadRef.current();
      return;
    }
    let gestart = false;
    let timer: number | undefined;
    let idle: number | undefined;

    const opruimen = () => {
      for (const naam of INTERACTIE_EVENTS) window.removeEventListener(naam, start);
      window.removeEventListener("load", naLaden);
      if (timer !== undefined) window.clearTimeout(timer);
      if (idle !== undefined && "cancelIdleCallback" in window) window.cancelIdleCallback(idle);
    };
    function start() {
      if (gestart) return;
      gestart = true;
      opruimen();
      laadRef.current();
    }
    function naLaden() {
      timer = window.setTimeout(() => {
        if ("requestIdleCallback" in window) idle = window.requestIdleCallback(start, { timeout: 3000 });
        else start();
      }, rustMs);
    }

    for (const naam of INTERACTIE_EVENTS) window.addEventListener(naam, start, { passive: true });
    if (document.readyState === "complete") naLaden();
    else window.addEventListener("load", naLaden, { once: true });
    return opruimen;
  }, [direct, rustMs]);
}
